
import api from './api';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

// Track Supabase connection status
let isSupabaseAvailable = true;
let hasSupabaseFallbackWarned = false;

// Extended service for user operations including admin management
const userAuthService = {
  // Helper method to check Supabase availability
  _checkSupabaseAvailability: async (): Promise<boolean> => {
    try {
      // Simple test query
      const { data, error } = await supabase.from('profiles').select('id').limit(1);
      if (error) {
        console.warn('Supabase availability check failed:', error);
        isSupabaseAvailable = false;
        return false;
      }
      
      isSupabaseAvailable = true;
      return true;
    } catch (error) {
      console.error('Error checking Supabase availability:', error);
      isSupabaseAvailable = false;
      return false;
    }
  },

  // Submit patient information for appointment booking
  submitPatientInfo: async (patientData: any) => {
    try {
      // Try Supabase first if it was previously available
      if (isSupabaseAvailable) {
        try {
          const { data, error } = await supabase
            .from('patients')
            .insert(patientData)
            .select()
            .single();
            
          if (error) {
            console.error('Error submitting patient info to Supabase:', error);
            throw error;
          }
          
          return data;
        } catch (supabaseError) {
          console.warn('Failed to submit patient to Supabase, falling back to API');
          isSupabaseAvailable = false;
          
          // Show warning toast only once - FIXED: Using the correct toast format
          if (!hasSupabaseFallbackWarned) {
            toast("Connection notice", {
              description: "Using backup connection method"
            });
            hasSupabaseFallbackWarned = true;
          }
        }
      }
      
      // Fall back to API
      const response = await api.post('/patients', patientData);
      return response.data;
    } catch (error) {
      console.error('Error submitting patient info:', error);
      throw error;
    }
  },
  
  // Get patient by ID
  getPatient: async (patientId: string) => {
    try {
      // Try Supabase first if it was previously available
      if (isSupabaseAvailable) {
        try {
          const { data, error } = await supabase
            .from('patients')
            .select()
            .eq('id', patientId)
            .single();
            
          if (error) {
            console.error('Error getting patient from Supabase:', error);
            throw error;
          }
          
          return data;
        } catch (supabaseError) {
          console.warn('Failed to get patient from Supabase, falling back to API');
          isSupabaseAvailable = false;
        }
      }
      
      // Fall back to API
      const response = await api.get(`/patients/${patientId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting patient:', error);
      throw error;
    }
  },
  
  // Store guest patient info in local storage
  storeGuestInfo: (patientData: any) => {
    localStorage.setItem('guest_patient_info', JSON.stringify(patientData));
    return patientData;
  },
  
  // Get guest patient info from local storage
  getGuestInfo: () => {
    const info = localStorage.getItem('guest_patient_info');
    return info ? JSON.parse(info) : null;
  },
  
  // Clear guest patient info from local storage
  clearGuestInfo: () => {
    localStorage.removeItem('guest_patient_info');
  },
  
  // Admin functions for user management
  adminCreateStaffUser: async (userData: AdminCreatedUser) => {
    try {
      // Check Supabase availability
      const supabaseAvailable = await userAuthService._checkSupabaseAvailability();
      
      if (supabaseAvailable) {
        try {
          // Try creating user in Supabase Auth and admin_users table
          const { data: adminData, error: adminError } = await supabase.functions.invoke('create-admin-user', {
            body: userData
          });
          
          if (adminError) {
            console.error('Supabase Edge Function error:', adminError);
            throw adminError;
          }
          
          return adminData;
        } catch (supabaseError) {
          console.warn('Falling back to API for creating staff user:', supabaseError);
          isSupabaseAvailable = false;
        }
      }
      
      // Fall back to API endpoint
      try {
        const response = await api.post('/auth/admin/users', userData);
        return response.data;
      } catch (error: any) {
        // Handle network errors in development with mock data
        if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
          console.warn('Using mock create user response due to network error. In production, this would fail.');
          
          // Mock response for development
          return {
            message: 'User created successfully (mock response)',
            userId: Math.floor(Math.random() * 1000) + 10,
            username: userData.username,
            role: userData.role,
            fullName: userData.fullName
          };
        }
        console.error('Error creating staff user:', error);
        throw error;
      }
    } catch (error) {
      console.error('Staff user creation failed:', error);
      throw error;
    }
  },
  
  getStaffUsers: async () => {
    try {
      // Check Supabase availability
      const supabaseAvailable = await userAuthService._checkSupabaseAvailability();
      
      if (supabaseAvailable) {
        try {
          // Try Supabase function first
          const { data: adminUsers, error: supabaseError } = await supabase.functions.invoke('get-admin-users');
          
          if (supabaseError) {
            console.error('Supabase function error:', supabaseError);
            throw supabaseError;
          }
          
          return adminUsers;
        } catch (error) {
          console.warn('Falling back to API for fetching staff users:', error);
          isSupabaseAvailable = false;
        }
      }
      
      // Fall back to API endpoint
      try {
        const response = await api.get('/auth/admin/users');
        return response.data;
      } catch (error: any) {
        // Handle network errors in development with mock data
        if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
          console.warn('Using mock staff users data due to network error. In production, this would fail.');
          
          // Return mock staff users for development
          return {
            users: [
              {
                id: "1",
                username: "admin",
                fullName: "Admin User",
                role: "admin",
                email: "admin@example.com",
                active: true,
                createdAt: new Date().toISOString()
              },
              {
                id: "2",
                username: "staff1",
                fullName: "Staff Member",
                role: "staff",
                email: "staff@example.com",
                active: true,
                createdAt: new Date().toISOString()
              },
              {
                id: "3",
                username: "clinic1",
                fullName: "Clinic User",
                role: "clinic",
                email: "clinic@example.com",
                active: true,
                createdAt: new Date().toISOString()
              }
            ]
          };
        }
        console.error('Error fetching staff users:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to get staff users:', error);
      throw error;
    }
  },
  
  getStaffUserById: async (userId: string) => {
    try {
      const response = await api.get(`/auth/admin/users/${userId}`);
      return response.data;
    } catch (error: any) {
      // Handle network errors in development with mock data
      if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        if (userId === "1") {
          return {
            id: "1",
            username: "admin",
            fullName: "Admin User",
            role: "admin",
            email: "admin@example.com",
            active: true,
            createdAt: new Date().toISOString(),
            permissions: []
          };
        } else if (userId === "2") {
          return {
            id: "2",
            username: "staff1",
            fullName: "Staff Member",
            role: "staff",
            email: "staff@example.com",
            active: true,
            createdAt: new Date().toISOString(),
            permissions: []
          };
        } else if (userId === "3") {
          return {
            id: "3",
            username: "clinic1",
            fullName: "Clinic User",
            role: "clinic",
            email: "clinic@example.com",
            active: true,
            createdAt: new Date().toISOString(),
            permissions: []
          };
        }
      }
      console.error('Error fetching staff user:', error);
      throw error;
    }
  },
  
  updateStaffUser: async (userId: string, updates: Partial<AdminCreatedUser>) => {
    try {
      const response = await api.put(`/auth/admin/users/${userId}`, updates);
      return response.data;
    } catch (error: any) {
      // Handle network errors in development
      if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        console.warn('Using mock update response due to network error. In production, this would fail.');
        
        // In development mode, update the mock data in localStorage to simulate persistence
        const staffData = localStorage.getItem('mock_staff_users');
        if (staffData) {
          const data = JSON.parse(staffData);
          const userIndex = data.users.findIndex((u: any) => u.id === userId);
          if (userIndex !== -1) {
            data.users[userIndex] = {
              ...data.users[userIndex],
              ...updates,
            };
            localStorage.setItem('mock_staff_users', JSON.stringify(data));
          }
        }
        
        return { message: 'User updated successfully (mock response)' };
      }
      console.error('Error updating staff user:', error);
      throw error;
    }
  },
  
  deleteStaffUser: async (userId: string) => {
    try {
      const response = await api.delete(`/auth/admin/users/${userId}`);
      return response.data;
    } catch (error: any) {
      // Handle network errors in development
      if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        console.warn('Using mock delete response due to network error. In production, this would fail.');
        
        // In development mode, update the mock data in localStorage to simulate deletion
        const staffData = localStorage.getItem('mock_staff_users');
        if (staffData) {
          const data = JSON.parse(staffData);
          data.users = data.users.filter((u: any) => u.id !== userId);
          localStorage.setItem('mock_staff_users', JSON.stringify(data));
        }
        
        return { message: 'User deleted successfully (mock response)' };
      }
      console.error('Error deleting staff user:', error);
      throw error;
    }
  },
  
  resetStaffPassword: async (userId: string, newPassword?: string) => {
    try {
      const data = newPassword ? { newPassword } : {};
      const response = await api.post(`/auth/admin/users/${userId}/reset-password`, data);
      return response.data;
    } catch (error: any) {
      // Handle network errors in development
      if (error.code === 'ERR_NETWORK' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        console.warn('Using mock password reset response due to network error. In production, this would fail.');
        return { message: 'Password reset successfully (mock response)' };
      }
      console.error('Error resetting staff password:', error);
      throw error;
    }
  },
  
  // Helper method to reset connection warnings
  resetConnectionWarnings: () => {
    hasSupabaseFallbackWarned = false;
  },
  
  // Helper method to store mock data for development persistence
  _storeMockStaffUsers: (users: any[]) => {
    if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
      localStorage.setItem('mock_staff_users', JSON.stringify({ users }));
    }
  }
};

// Type definitions for admin-created users
export interface AdminCreatedUser {
  username: string;
  password: string;
  role: 'admin' | 'staff' | 'clinic';
  email?: string;
  fullName: string;
  permissions?: string[];
  active?: boolean;
}

export default userAuthService;
